import { useEffect, useRef, useState } from "react";
import MonitorUp from "~icons/ph/monitor-arrow-up";
import VideoOff from "~icons/ph/video-camera-slash";
import { Avatar } from "../ui/components";
import { watchVoiceLevel } from "./voice-level";
import { Voiceprint } from "./voiceprint";
import { t } from "../i18n";
import type { Person } from "../../shared/types";

export function VideoPreview({
  stream,
  label,
  mirrored = false,
  onShape,
}: {
  stream: MediaStream | null;
  label: string;
  mirrored?: boolean;
  // Width over height, once known and whenever the picture turns.
  onShape?: (shape: number) => void;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    video.srcObject = stream;
    if (stream) void video.play().catch(() => {});
    return () => {
      video.srcObject = null;
    };
  }, [stream]);
  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted
      aria-label={label}
      className={mirrored ? "mirrored" : ""}
      onResize={({ currentTarget: { videoWidth, videoHeight } }) => {
        if (videoHeight) onShape?.(videoWidth / videoHeight);
      }}
    />
  );
}

// The other person's lines around their avatar: they rise and fall with their
// voice while the call is connected, and stay quiet and still otherwise.
export function CallPortrait({
  person,
  voice,
}: {
  person: Person;
  voice: MediaStream | null;
}) {
  const print = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = print.current;
    if (!node || !voice) return;
    const stop = watchVoiceLevel(voice, (level) =>
      node.style.setProperty("--level", (0.12 + 0.88 * level).toFixed(3)),
    );
    return () => {
      stop();
      node.style.removeProperty("--level");
    };
  }, [voice]);
  return (
    <Voiceprint ref={print} id={person.id} live={!!voice}>
      <Avatar person={person} large />
    </Voiceprint>
  );
}

export function CallVideo({
  person,
  localStream,
  remoteStream,
  remoteAudio,
  cameraOff,
  remoteCameraOff,
  connected,
  sharingScreen,
  remoteSharingScreen,
}: {
  person: Person;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  remoteAudio: MediaStream | null;
  cameraOff: boolean;
  remoteCameraOff: boolean;
  connected: boolean;
  sharingScreen: boolean;
  remoteSharingScreen: boolean;
}) {
  const showingRemote = connected && remoteStream && !remoteCameraOff;
  // The self-view tile follows its camera, so a sideways camera has no bars.
  const [selfShape, setSelfShape] = useState<number>();
  return (
    <div
      className={`video-stage ${sharingScreen ? "sharing-local" : ""} ${remoteSharingScreen ? "sharing-remote" : ""}`}
    >
      <div className={`remote-video ${showingRemote ? "has-video" : ""}`}>
        <VideoPreview
          stream={remoteStream}
          label={t(remoteSharingScreen ? "شاشة {name}" : "فيديو {name}", {
            name: person.name,
          })}
        />
        {!showingRemote && (
          <div className="video-placeholder">
            <CallPortrait
              person={person}
              voice={connected ? remoteAudio : null}
            />
            {connected && (
              <span>
                {remoteCameraOff
                  ? t("كاميرا الطرف الآخر متوقفة")
                  : t("في انتظار الفيديو…")}
              </span>
            )}
          </div>
        )}
        <span className="video-label">
          {remoteSharingScreen
            ? t("{name} يشارك الشاشة", { name: person.name })
            : person.name}
        </span>
      </div>
      <div
        className={`local-video ${selfShape && selfShape > 1 ? "landscape" : ""}`}
        style={selfShape ? { aspectRatio: selfShape } : undefined}
      >
        {localStream && (sharingScreen || !cameraOff) ? (
          <VideoPreview
            stream={localStream}
            label={t(sharingScreen ? "معاينة شاشتك" : "معاينة كاميرتك")}
            // A camera on the back of a phone shows the world as it is.
            mirrored={
              !sharingScreen &&
              localStream.getVideoTracks()[0]?.getSettings().facingMode !==
                "environment"
            }
            onShape={setSelfShape}
          />
        ) : (
          <div className="local-video-placeholder">
            <VideoOff width={21} height={21} />
            <span>{cameraOff ? t("الكاميرا متوقفة") : t("كاميرتك")}</span>
          </div>
        )}
        <span className="video-label">
          {t(sharingScreen ? "شاشتك" : "أنت")}
        </span>
      </div>
      {sharingScreen && (
        <span className="sharing-notice">
          <MonitorUp width={14} height={14} />
          {t("أنت تشارك شاشتك")}
        </span>
      )}
    </div>
  );
}
